import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from "../../lib/http";

// The backend's machine-readable reason (utils/validation-error.js emits errors[0].code) rides in
// meta, not payload: every caller already treats a rejected sign-in payload as the string it shows,
// so widening it to an object would silently turn three toasts into "[object Object]".
const signInFailure = (error, rejectWithValue) => {
  const failure = error.response?.data?.error?.errors?.[0]
  return rejectWithValue(failure?.message || 'Failed to sign in', { code: failure?.code })
}

export const signIn = createAsyncThunk('auth/signIn', async (credentials, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post('/sign-in', credentials);
    return response.data;
  } catch (error) {
    return signInFailure(error, rejectWithValue);
  }
});

export const signInInternal = createAsyncThunk('auth/signInInternal', async (credentials, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post('/sign-in/internal', credentials)
    return response.data
  } catch (error) {
    return signInFailure(error, rejectWithValue)
  }
})

// Portal forgot-password (contacts via secondaryEmail, co-applicants via email).
// Backend narrows the resolvable role set; admin emails on this endpoint always
// return the generic "User with this email does not exist" 400 (FR-002, FR-007).
export const requestCode = createAsyncThunk('auth/requestCode', async (credentials, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/request-code/email?flow=forgot`, credentials);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error?.errors?.[0]?.message || 'Failed to reset password';
    return rejectWithValue(errorMessage);
  }
});

export const forgotPassword = createAsyncThunk('auth/forgotPassword', async (credentials, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/forgot-password`, credentials);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error?.errors?.[0]?.message || 'Failed to reset password';
    return rejectWithValue(errorMessage);
  }
});

// Internal/admin forgot-password (admins, customers, staff, affiliates,
// affiliateGroupsAdmin only). Backend rejects contact / co-applicant emails
// with the same generic 400 to prevent cross-portal account enumeration
// (FR-003, FR-007).
export const requestCodeInternal = createAsyncThunk('auth/requestCodeInternal', async (credentials, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/request-code/email/internal?flow=forgot`, credentials);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error?.errors?.[0]?.message || 'Failed to reset password';
    return rejectWithValue(errorMessage);
  }
});

export const forgotPasswordInternal = createAsyncThunk('auth/forgotPasswordInternal', async (credentials, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/forgot-password/internal`, credentials);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error?.errors?.[0]?.message || 'Failed to reset password';
    return rejectWithValue(errorMessage);
  }
});

export const checkCodeReq = createAsyncThunk('auth/checkCodeReq', async (credentials, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/check-code`, credentials);
    return response.data;
  } catch (error) {
    // The non-optional path this took threw inside its own catch on any {message}-shaped body,
    // which the throttle response produces — so the rejection carried no message and the page
    // showed nothing.
    const data = error.response?.data;
    const errorMessage = data?.message
      || (typeof data?.error?.errors === 'string' ? data.error.errors : null)
      || data?.error?.errors?.[0]?.message
      || 'Invalid or expired confirmation code';
    return rejectWithValue(errorMessage);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    status: 'idle',
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export default authSlice.reducer;
